import React, { CSSProperties, FC, ForwardedRef, forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import styled from 'styled-components';
import { debounce, omit } from 'lodash';

import { Icon } from '../icon';
import theme from '../../../shared/theme';
import { isMobile, T } from '../helpers/helpers';
import { BasicButton, Button, GhostButton } from '../buttons';
import CopyIcon from '../svg/copyIcon';
import EyeSolid from '../svg/eyeSolid';
import LowVisionSolid from '../svg/lowVisionSolid';
import MaterialSearchSvg from 'src/shared/material_icons/action/search.svg';
import MaterialCloseSvg from 'src/shared/material_icons/navigation/close.svg';
import TrashIcon from 'src/shared/material_icons/action/delete.svg';
import PlusIcon from 'src/shared/material_icons/content/add.svg';
import ValidationMessage from 'src/components/widgets/validation/validationMessage';
import { useValidationControl, ValidationControlProps } from 'src/shared/validation';

export interface InputProps extends React.ComponentPropsWithoutRef<'input'>, ValidationControlProps<string> {
    fullWidth?: boolean;
    isError?: boolean;
    simplified?: boolean;
}

const InputBase = styled.input.withConfig({
    shouldForwardProp: (prop) => !['fullWidth', 'isError', 'simplified'].includes(prop)
})<InputProps>`
    color: ${(props) => props.theme.input.color};
    border: 1px #ddd solid;
    border-radius: 4px;
    padding: 4px 10px;
    font-family: ${(props) => props.theme.app.font};
    font-size: ${(props) => props.theme.input.textSize};
    height: 32px;

    &::placeholder {
        font-size: 13px;
    }

    &:focus,
    &:active {
        outline: none;
        ${(props) => (props.isError ? 'border-color: #DE1C22;' : `border-color: #ddd`)}
    }

    ${(props) => (props.fullWidth ? 'width: 100%;' : '')}
    ${(props) => (props.readOnly ? 'background-color: transparent;' : '')}

	${(props) => props.isError && 'border-color: #DE1C22;'}
	${(props) => props.isError && 'color: #DE1C22;'}
	${(props) => props.isError && 'box-shadow: 0 0 0 2px rgba(222, 28, 34, 0.2);'}
`;

const _Input = React.forwardRef<HTMLInputElement, InputProps>((props, ref) => {
    const { validationKey, validate, validationMessage, message, validationLabel, validationValue, isError, value, className, simplified, ...inputProps } =
        props;
    const valueToValidate = validationValue?.toString() ?? value?.toString() ?? '';
    const validation = useValidationControl<HTMLInputElement, string>(
        { validationKey, validate, validationMessage: validationMessage ?? message, validationLabel, validationValue: valueToValidate },
        valueToValidate,
        ref
    );

    return (
        <>
            <InputBase
                {...inputProps}
                ref={validation.setRef}
                value={value}
                className={className}
                isError={isError || validation.isValidationError}
                simplified={simplified}
            />
            {validation.validationKey && <ValidationMessage validationKey={validation.validationKey} deferUntilSubmit />}
        </>
    );
});

export const Input = styled(_Input)``;

interface InputWithAddRemoveProps extends InputProps {
    removeCallback?: () => void;
    addCallback?: () => void;
    showAdd?: boolean;
    showDelete?: boolean;
}

const InputWithAddRemoveContainer = styled.div`
    position: relative;
`;

const InputWithAddRemoveButtons = styled.div`
    display: flex;
    gap: 4px;
    position: absolute;
    right: 2px;
    top: 2px;
    z-index: 2;

    ${Button} {
        width: 24px;
        height: 24px;
        min-width: 24px;
        min-height: 24px;
    }
`;

export const InputWithAddRemove: FC<InputWithAddRemoveProps> = (props) => {
    const { addCallback, removeCallback, showAdd, showDelete, ...inputProps } = props;

    return (
        <InputWithAddRemoveContainer>
            <Input {...(inputProps as InputProps)} />
            <InputWithAddRemoveButtons>
                {showAdd !== false && (
                    <GhostButton
                        type='button'
                        $size='small'
                        $type='icon'
                        onClick={() => {
                            addCallback?.();
                        }}
                    >
                        <Icon>
                            <PlusIcon />
                        </Icon>
                    </GhostButton>
                )}
                {showDelete !== false && (
                    <GhostButton
                        type='button'
                        $size='small'
                        $type='icon'
                        onClick={() => {
                            removeCallback?.();
                        }}
                    >
                        <Icon>
                            <TrashIcon />
                        </Icon>
                    </GhostButton>
                )}
            </InputWithAddRemoveButtons>
        </InputWithAddRemoveContainer>
    );
};

const SearchInputContainer = styled.div<{ $hasClear?: boolean }>`
    position: relative;

    ${Input} {
        width: 100%;
        padding-left: 40px;
        ${(props) => props.$hasClear && 'padding-right: 32px;'}
    }

    /* La crocetta di sistema è grigia di suo, cambia disegno da un browser all'altro e su Firefox
       non c'è: dove mettiamo la nostra, quella del browser si toglie di mezzo. */
    ${Input}::-webkit-search-cancel-button {
        display: none;
    }
`;

const SearchInputClearButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    padding: 0;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: ${(props) => props.theme.app.primary};
    cursor: pointer;

    svg {
        width: 14px;
        height: 14px;
        fill: currentColor;
    }

    &:hover {
        color: ${(props) => props.theme.app.textColor};
        background: ${(props) => props.theme.app.backgroundToolbar};
    }

    &:focus-visible {
        outline: 2px solid ${(props) => props.theme.app.secondary};
        outline-offset: 1px;
    }
`;

/* COMPACT — the dense variant the properties panels are built on: a field that has to sit in a
   column barely wider than its label. Same Input, one size down. */
export const CompactInput = styled(Input)`
    min-width: 0;
    height: 28px;
    flex: 1;
    padding: 3px 8px;
    font-size: 12px;
`;

export const SearchInput = React.forwardRef(
    (
        props: InputProps &
            React.ComponentProps<'input'> & {
                onSearch?: (filter: string) => void;
                containerStyle?: CSSProperties;
            } & {
                rightElement?: React.ReactNode;
                /** Svuota il campo: la crocetta compare solo quando c'è qualcosa da cancellare. */
                onClear?: () => void;
            },
        ref: ForwardedRef<HTMLInputElement>
    ) => {
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            if (props.onChange) props.onChange(e);

            handleSearch(e.currentTarget.value);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
        const handleSearch = useCallback(
            debounce((filter: string) => {
                if (props.onSearch) props.onSearch(filter);
            }, 400),
            []
        );

        const showClear = !!props.onClear && !!props.value;

        return (
            <SearchInputContainer style={props.containerStyle} $hasClear={showClear}>
                <SearchInputIcon>
                    <SearchInputIconSvg>
                        <MaterialSearchSvg />
                    </SearchInputIconSvg>
                </SearchInputIcon>
                <Input {...omit(props, ['onSearch', 'ref', 'rightElement', 'containerStyle', 'onClear'])} ref={ref} onChange={handleChange} />

                <SearchInputRightElementContainer>
                    {showClear && (
                        <SearchInputClearButton type='button' aria-label={T._('Clear the search', 'Admin')} onClick={() => props.onClear?.()}>
                            <MaterialCloseSvg />
                        </SearchInputClearButton>
                    )}
                    {props.rightElement}
                </SearchInputRightElementContainer>
            </SearchInputContainer>
        );
    }
);

const SearchInputRightElementContainer = styled.div`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 10px;
    color: ${(props) => props.theme.app.primary};
`;

const SearchInputIcon = styled.div`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: 10px;
    color: #71717a;
    display: flex;
    align-items: center;
`;

const SearchInputIconSvg = styled(Icon)``;

export const Command = styled.div`
    display: inline-block;
    padding: 3px 5px;
    border-radius: 3px;
    background-color: #eee;
    font-size: 10px;
`;

const CopyInputContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 4px;

    ${Input} {
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

export const CopyInput: FC<InputProps> = (props: InputProps) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const tooltipTimeout = useRef<any | undefined>(undefined);

    const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        const { value } = props;
        if (value) {
            if ('clipboard' in navigator) {
                return navigator.clipboard
                    .writeText(value as string)
                    .then(() => {
                        setShowTooltip(true);
                        if (tooltipTimeout.current) {
                            clearTimeout(tooltipTimeout.current);
                        }
                        tooltipTimeout.current = setTimeout(() => {
                            setShowTooltip(false);
                        }, 2000);
                    })
                    .catch(() => {
                        alert('Failed to copy text');
                    });
            } else {
                return document.execCommand('copy', true, value as string);
            }
        }
    };

    return (
        <CopyInputContainer>
            <Input readOnly={true} {...(props as InputProps)} />
            <GhostButton $size='small' $type='icon' onClick={handleClick} title={T._('Copy', 'Admin')}>
                <Icon>
                    <CopyIcon />
                </Icon>
            </GhostButton>
        </CopyInputContainer>
    );
};

export const _FileInputContainer = styled.div`
    display: flex;
    align-items: center;

    input {
        display: none;
    }

    span {
        margin-left: 10px;
    }
`;

type SingleFileInputProps = {
    multiple?: false;
    accept?: string;
    disabled?: boolean;
    fileName?: string;
    label?: React.ReactNode;
    onChange: (file: File | null) => void;
    ref?: any;
};
type MultipleFileInputProps = {
    multiple: true;
    accept?: string;
    disabled?: boolean;
    fileName?: string;
    label?: React.ReactNode;
    onChange: (files: FileList | null) => void;
    ref?: any;
};
export type FileInputProps = SingleFileInputProps | MultipleFileInputProps;

export const FileInput: FC<FileInputProps> = forwardRef<{ reset: () => void }, FileInputProps>(
    ({ multiple, disabled, accept, fileName, label, onChange }, ref) => {
        const inputRef = useRef<HTMLInputElement>(null);

        const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = e.currentTarget.files;
            if (files && files.length > 0) {
                if (multiple) {
                    onChange(files);
                    return;
                }
                onChange(files[0]);
            } else {
                onChange(null);
            }
        };

        const reset = useCallback(() => {
            if (inputRef.current) {
                inputRef.current.value = '';
            }
        }, []);

        useImperativeHandle(
            ref,
            () => ({
                reset: reset
            }),
            [reset]
        );

        return (
            <_FileInputContainer>
                <input type='file' multiple={multiple} ref={inputRef} onChange={handleOnChange} accept={accept} />
                <BasicButton disabled={disabled} onClick={() => inputRef.current?.click()}>
                    {label || T._('Choose file', 'Admin')}
                </BasicButton>
                {fileName && <span>{fileName}</span>}
            </_FileInputContainer>
        );
    }
);

export const FormDate = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;

    label {
        margin: 5px 0 5px 0 !important;
        font: ${theme.app.font};
    }

    input {
        width: ${!isMobile() ? '180px' : '100%'};
        padding: ${!isMobile() ? '0 16px' : '0 7px'};
        margin: ${!isMobile() ? '5px 0 5px 0 !important' : ''};
    }
`;

export const StyledDatePicker = styled.input<{ $isError?: boolean }>`
    border: 1px solid;
    border-width: 1px;
    border-color: ${(props) => (props.$isError ? '#DE1C22' : 'rgb(222, 222, 222)')};
    border-radius: 4px;
    width: auto;
    height: 40px;
    color: #455a64;
    font-size: 12px;
    font-family: ${theme.app.font};
    box-shadow: ${(props) => (props.$isError ? '0 0 0 2px rgba(222, 28, 34, 0.2)' : 'none')};

    &:focus,
    &:active {
        outline: none;
        border-color: ${(props) => (props.$isError ? '#DE1C22' : theme.app.primary)};
    }
`;

type DateType = 'datetime-local' | 'date' | 'time';

export type DatePickerInputProps = {
    label?: string;
    value?: string;
    type?: DateType;
    onChange: (date: string) => void;
};

export const DatePickerInput: FC<DatePickerInputProps> = ({ label, value, type, onChange }) => {
    const [date, setDate] = useState<string>(value ?? '');
    const dateType = type ?? 'datetime-local';

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDate(e.currentTarget.value);
        onChange(e.currentTarget.value);
    };

    return (
        <FormDate>
            {label && <label>{T._(`${label}`, 'Admin')}</label>}
            <StyledDatePicker type={dateType} value={date} onChange={handleChange}></StyledDatePicker>
        </FormDate>
    );
};

const PasswordInputContainer = styled.div`
    position: relative;

    ${Icon} {
        position: absolute;
        top: calc(50% - 12px);
        right: 10px;
        cursor: pointer;
        width: 20px;
        height: 20px;
    }
`;

export const PasswordInput = React.forwardRef<HTMLInputElement, InputProps>((props, ref) => {
    const [showPassword, setShowPassword] = useState(false);
    const [type, setType] = useState('password');
    const handleClick = async (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        setShowPassword((prev) => {
            prev = !showPassword;
            setType(prev ? 'text' : 'password');
            return prev;
        });
    };

    return (
        <PasswordInputContainer>
            <Input {...(props as InputProps)} ref={ref} type={type} />
            <Icon onClick={handleClick}>{showPassword ? <LowVisionSolid /> : <EyeSolid />}</Icon>
        </PasswordInputContainer>
    );
});
